// src/StripeContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { fetchStripeConfig } from './services/stripeServices';

interface StripeContextType {
  stripe: Stripe | null;
  isLoading: boolean;
  error: string | null;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export const StripeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        setIsLoading(true);
        
        // Get Stripe config from our GraphQL API
        const config = await fetchStripeConfig();
        
        // Load the Stripe.js library with the publishable key
        const stripeInstance = await loadStripe(config.publishableKey);
        
        if (!stripeInstance) {
          throw new Error('Failed to initialize Stripe');
        }
        
        setStripe(stripeInstance);
        setError(null);
      } catch (err) {
        console.error('Failed to initialize Stripe:', err);
        setError('Could not load payment system. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeStripe();
  }, []);

  return (
    <StripeContext.Provider value={{ stripe, isLoading, error }}>
      {children}
    </StripeContext.Provider>
  );
};

export const useStripe = (): StripeContextType => {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};